import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  // useCallback gives showToast a stable reference so context consumers
  // (Admin, Cashier) don't re-render every time a toast is added/removed.
  const showToast = useCallback((message, type = "error") => {
    const id = ++idRef.current;
    // Clear previous success toasts to prevent stacking
    setToasts((prev) => {
      const filtered = type === "success" ? prev.filter((t) => t.type !== "success") : prev;
      return [...filtered, { id, message, type }];
    });
    const duration = type === "success" ? 2000 : 4000;
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, duration);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Stable context value — only changes if showToast itself changes (never)
  const contextValue = useMemo(() => showToast, [showToast]);

  // Render the toast portal directly on document.body so it sits above
  // every modal, overlay, and stacking context in the app.
  const portal =
    toasts.length > 0
      ? ReactDOM.createPortal(
          <div className="toast-container">
            {toasts.map((toast) => (
              <div
                key={toast.id}
                className={`toast toast-${toast.type}`}
                role={toast.type === "error" ? "alert" : "status"}
                aria-live={toast.type === "error" ? "assertive" : "polite"}
                aria-atomic="true"
              >
                <span>{toast.message}</span>
                <button
                  className="toast-dismiss"
                  onClick={() => dismissToast(toast.id)}
                  aria-label="Dismiss notification"
                >
                  ×
                </button>
              </div>
            ))}
          </div>,
          document.body
        )
      : null;

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {portal}
    </ToastContext.Provider>
  );
}

/** Call showToast(message, type) where type is "error" | "success" | "info" */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}
